import React, { createContext, useContext, useState, useEffect } from 'react'

type Language = 'en' | 'es' | 'fr' | 'de' | 'pt' | 'ja' | 'zh'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    'nav.home': 'Home',
    'nav.evidence': 'Evidence Library',
    'nav.subprocessors': 'Subprocessors',
    'nav.status': 'Status',
    'hero.title': 'Trust Center',
    'hero.subtitle': 'Security & Compliance Documentation',
    'documents.title': 'Document Library',
    'documents.download': 'Download',
    'documents.view': 'View',
    'schedule.review': 'Schedule Security Review',
    'schedule.book': 'Book a Call',
    'contact.email': 'Email',
    'contact.company': 'Company',
    'contact.submit': 'Submit',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.save': 'Save',
    'common.cancel': 'Cancel'
  },
  es: {
    'nav.home': 'Inicio',
    'nav.evidence': 'Biblioteca de Evidencias',
    'nav.subprocessors': 'Subprocesadores',
    'nav.status': 'Estado',
    'hero.title': 'Centro de Confianza',
    'hero.subtitle': 'Documentación de Seguridad y Cumplimiento',
    'documents.title': 'Biblioteca de Documentos',
    'documents.download': 'Descargar',
    'documents.view': 'Ver',
    'schedule.review': 'Programar Revisión de Seguridad',
    'schedule.book': 'Reservar una Llamada',
    'contact.email': 'Correo Electrónico',
    'contact.company': 'Empresa',
    'contact.submit': 'Enviar',
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar'
  },
  fr: {
    'nav.home': 'Accueil',
    'nav.evidence': 'Bibliothèque de Preuves',
    'nav.subprocessors': 'Sous-traitants',
    'nav.status': 'Statut',
    'hero.title': 'Centre de Confiance',
    'hero.subtitle': 'Documentation de Sécurité et Conformité',
    'documents.title': 'Bibliothèque de Documents',
    'documents.download': 'Télécharger',
    'documents.view': 'Voir',
    'schedule.review': 'Planifier un Examen de Sécurité',
    'schedule.book': 'Réserver un Appel',
    'contact.email': 'Email',
    'contact.company': 'Entreprise',
    'contact.submit': 'Soumettre',
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.save': 'Sauvegarder',
    'common.cancel': 'Annuler'
  },
  de: {
    'nav.home': 'Startseite',
    'nav.evidence': 'Beweisbibliothek',
    'nav.subprocessors': 'Unterauftragnehmer',
    'nav.status': 'Status',
    'hero.title': 'Vertrauenszentrum',
    'hero.subtitle': 'Sicherheits- und Compliance-Dokumentation',
    'documents.title': 'Dokumentenbibliothek',
    'documents.download': 'Herunterladen',
    'documents.view': 'Ansehen',
    'schedule.review': 'Sicherheitsüberprüfung Planen',
    'schedule.book': 'Anruf Buchen',
    'contact.email': 'E-Mail',
    'contact.company': 'Unternehmen',
    'contact.submit': 'Einreichen',
    'common.loading': 'Wird geladen...',
    'common.error': 'Fehler',
    'common.save': 'Speichern',
    'common.cancel': 'Abbrechen'
  },
  pt: {
    'nav.home': 'Início',
    'nav.evidence': 'Biblioteca de Evidências',
    'nav.subprocessors': 'Subprocessadores',
    'nav.status': 'Status',
    'hero.title': 'Centro de Confiança',
    'hero.subtitle': 'Documentação de Segurança e Conformidade',
    'documents.title': 'Biblioteca de Documentos',
    'documents.download': 'Baixar',
    'documents.view': 'Ver',
    'schedule.review': 'Agendar Revisão de Segurança',
    'schedule.book': 'Agendar Chamada',
    'contact.email': 'Email',
    'contact.company': 'Empresa',
    'contact.submit': 'Enviar',
    'common.loading': 'Carregando...',
    'common.error': 'Erro',
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar'
  },
  ja: {
    'nav.home': 'ホーム',
    'nav.evidence': 'エビデンスライブラリ',
    'nav.subprocessors': 'サブプロセッサー',
    'nav.status': 'ステータス',
    'hero.title': 'トラストセンター',
    'hero.subtitle': 'セキュリティとコンプライアンスのドキュメント',
    'documents.title': 'ドキュメントライブラリ',
    'documents.download': 'ダウンロード',
    'documents.view': '表示',
    'schedule.review': 'セキュリティレビューをスケジュール',
    'schedule.book': '通話を予約',
    'contact.email': 'メール',
    'contact.company': '会社',
    'contact.submit': '送信',
    'common.loading': '読み込み中...',
    'common.error': 'エラー',
    'common.save': '保存',
    'common.cancel': 'キャンセル'
  },
  zh: {
    'nav.home': '首页',
    'nav.evidence': '证据库',
    'nav.subprocessors': '子处理器',
    'nav.status': '状态',
    'hero.title': '信任中心',
    'hero.subtitle': '安全与合规文档',
    'documents.title': '文档库',
    'documents.download': '下载',
    'documents.view': '查看',
    'schedule.review': '安排安全审查',
    'schedule.book': '预约通话',
    'contact.email': '电子邮件',
    'contact.company': '公司',
    'contact.submit': '提交',
    'common.loading': '加载中...',
    'common.error': '错误',
    'common.save': '保存',
    'common.cancel': '取消'
  }
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('trust-center-language')
    return (saved as Language) || 'en'
  })

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    localStorage.setItem('trust-center-language', lang)
    document.documentElement.lang = lang
  }

  const t = (key: string): string => {
    return translations[language][key] || key
  }

  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return context
}

export const LanguageSelector = () => {
  const { language, setLanguage } = useLanguage()

  const languages: { code: Language; name: string; flag: string }[] = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ]

  return (
    <select
      value={language}
      onChange={(e) => setLanguage(e.target.value as Language)}
      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
    >
      {languages.map(lang => (
        <option key={lang.code} value={lang.code}>
          {lang.flag} {lang.name}
        </option>
      ))}
    </select>
  )
}
